import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  PersonalInfoInterface,
  schema as schemaYup,
} from '@/pages/signUp/signUpPersonalInfo/yupPersonInfoFields';
import React, { ReactElement } from 'react';
import {
  UserSignInPersonalInfoInterface,
  SignUpControllerInterface,
} from '../SignUpInterface';
import { UserDTO } from '@/data/DTOs/UserDTO';
import { dateFormatter } from '@/utils/dateFormatter';

export const SignUpPersonalInfoController = ({
  user,
  setUser,
  country,
}: SignUpControllerInterface & PersonalInfoInterface) => {
  const schema = schemaYup({
    country,
  });
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    trigger,
  } = useForm<UserSignInPersonalInfoInterface>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { formatFromDate } = dateFormatter();

  const getUserFieldValue = (user: UserDTO, fieldName: string): string => {
    if (fieldName in user) {
      const value = user[fieldName as keyof UserDTO];
      return value !== undefined ? String(value) : '';
    }
    return '';
  };

  const controlledTextView = (
    children: ReactElement,
    fieldName:
      | 'name'
      | 'surname'
      | 'phone'
      | 'birthDate'
      | 'email'
      | 'countryPhoneCode'
      | 'gender'
  ) => {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={getUserFieldValue(user, fieldName)}
        render={({ field }) =>
          React.cloneElement(children, {
            onValueChange: (value: string) => {
              setUser({
                ...user,
                [fieldName]: value,
              });
              field.onChange(value);
            },
            onChange: (value: Date | null) => {
              if (value == null) {
                setUser({
                  ...user,
                  [fieldName]: null,
                });
                return;
              }
              if (!isNaN(value.getTime()))
                setUser({
                  ...user,
                  [fieldName]: formatFromDate(value),
                });
              field.onChange(value);
            },
            error: !!errors[fieldName],
            helperText: errors[fieldName]?.message,
          })
        }
      />
    );
  };

  const triggerFieldUpdate = (fieldName: 'email', value: string) => {
    setValue(fieldName, value, { shouldValidate: true, shouldDirty: true });
  };

  return {
    errors,
    control,
    isValid,
    controlledTextView,
    handleSubmit,
    triggerFieldUpdate,
    trigger,
  };
};
